import * as React from 'react';
import { SentinelNode } from '@fluentui-copilot/chat-input-plugins';
import type { InitialConfigType, LexicalEditor } from '@fluentui-copilot/react-text-editor';
import { mergeRegister } from '@fluentui-copilot/react-text-editor';
import { useControllableState, useIsomorphicLayoutEffect } from '@fluentui/react-utilities';
import type { EditorInputProps } from '../EditorInput';
import { useLexicalStyles } from '../EditorInput';
import { useLexicalEditor } from './useLexicalEditor';
import { useCanShowPlaceholder } from './useCanShowPlaceholder';
import { mergeClasses } from '@fluentui/react-components';

export type UseLexicalContentEditableProps = Pick<EditorInputProps, 'customNodes' | 'disabled' | 'customTheme'> & {
  defaultValue?: () => void;
};
export type UseLexicalContentEditableResult = {
  canShowPlaceholder: boolean;
  disabled: boolean;
  spanRef: React.Ref<HTMLSpanElement>;
  lexicalEditor: LexicalEditor;
  lexicalInitialConfig: InitialConfigType;
};

export function useLexicalContentEditable(props: UseLexicalContentEditableProps): UseLexicalContentEditableResult {
  const { defaultValue, customTheme } = props;

  const customNodes = props.customNodes ? [...props.customNodes, SentinelNode] : [SentinelNode];

  // The disabled state can also be changed by lexical (e.g. by a custom plugin) so
  // we use `useControllableState` to coordinate
  const [disabled, setDisabled] = useControllableState({
    state: props.disabled,
    initialState: false,
  });

  const lexicalStyles = useLexicalStyles();
  const paragraphClassName = mergeClasses(lexicalStyles.paragraph, customTheme?.paragraph);

  const lexicalInitialConfig = {
    namespace: 'fai-EditorInput',
    onError: console.log,
    nodes: customNodes,
    editorState: defaultValue,
    editable: !disabled,
    theme: { ...customTheme, paragraph: paragraphClassName.trim() === '' ? undefined : paragraphClassName },
  };
  const lexicalEditor = useLexicalEditor(lexicalInitialConfig);

  const canShowPlaceholder = useCanShowPlaceholder(lexicalEditor);

  const spanRef = React.useCallback(
    (span: HTMLSpanElement | null) => {
      // Register the `input` span with lexical
      lexicalEditor.setRootElement(span);
    },
    [lexicalEditor],
  );

  // Update lexical when disabled changes
  useIsomorphicLayoutEffect(() => {
    lexicalEditor.setEditable(!disabled);
  }, [lexicalEditor]);

  useIsomorphicLayoutEffect(() => {
    return mergeRegister(
      lexicalEditor.registerEditableListener(isEditable => {
        setDisabled(!isEditable);
      }),
      lexicalEditor.registerRootListener(root => {
        if (!root) {
          return;
        }

        // Remove lexical's inline style so we can apply our own
        // Lexical needs the whitespace style to be either `pre` or `pre-wrap` to work correctly
        root.style.whiteSpace = '';
      }),
    );
  }, []);

  return { canShowPlaceholder, disabled, spanRef, lexicalEditor, lexicalInitialConfig };
}
